import { faInfoCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import AlertSummaryList from '@/pages/BillingReportResults/components/AlertSummaryList';

import './styles.css';
import type { Props } from './types';

/**
 * Alert Summary for the Billing Items list. Same panel as the legacy report
 * view's BillingReportErrorSummary, but fed by the billing items summary
 * endpoint instead of the loaded rows -- the grid uses ag-grid's infinite row
 * model, so only ~50 rows are ever in memory while these counts describe the
 * entire filtered set.
 */
const AlertSummaryPanel = ({ summary, isLoading, isError, onFilterByAlert }: Props) => {
  if (isError) {
    return (
      <div className="p-4 bg-white border border-gray-300 shadow" data-testid="errors_summary">
        <h2 className="text-lg font-bold">Alert Summary</h2>
        <p className="text-xs text-red-600">Failed to load the alert summary.</p>
      </div>
    );
  }

  if (isLoading || !summary) {
    return (
      <div className="p-4 bg-white border border-gray-300 shadow" data-testid="errors_summary">
        <h2 className="text-lg font-bold">Alert Summary</h2>
        <p className="text-xs text-gray-500">
          <FontAwesomeIcon icon={faSpinner} className="mr-1 animate-spin" />
          Counting alerts across the filtered items...
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white border border-gray-300 shadow" data-testid="errors_summary">
      <h2 className="text-lg font-bold">Alert Summary</h2>

      <p className="text-xs text-gray-500">
        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
        Covers every item matching the current filters, not just the rows on screen. Submitted and
        non-billable items are left out, along with alerts you&apos;ve ignored.
      </p>

      <h3 className="font-bold pt-2">Errors</h3>
      <AlertSummaryList groups={summary.errors} type="error" search={onFilterByAlert} />

      <h3 className="font-bold pt-2">Warnings</h3>
      <AlertSummaryList groups={summary.warnings} type="warning" search={onFilterByAlert} />
    </div>
  );
};

export default React.memo(AlertSummaryPanel);
